import type { ReactNode } from 'react'
import { NavLink, Navigate, Route, Routes } from 'react-router'
import { useAuth } from '../auth/AuthProvider'
import AttributesPage from './AttributesPage'
import CategoriesPage from './CategoriesPage'
import PlatformPage from './PlatformPage'
import DocumentsPage from './DocumentsPage'
import InventoryPage from './InventoryPage'
import MovementsPage from './MovementsPage'
import ProductCardPage from './ProductCardPage'
import ProductsPage from './ProductsPage'
import SalePage from './SalePage'
import CheckoutPage from './CheckoutPage'
import StoresPage from './StoresPage'
import ImportsPage from './ImportsPage'
import IndexPage from './IndexPage'
import WarehousesPage from './WarehousesPage'

/**
 * Оболочка административного приложения: верхняя панель, боковая навигация,
 * рабочая область.
 *
 * Замещается оболочкой Horizon UI при подключении шаблона (ДС-01, ДС-02);
 * состав разделов и правила видимости при этом сохраняются.
 */

type Section = {
  path: string
  label: string
  /** Раздел прав, определяющий видимость пункта (п. 4.2). */
  permission: string
  /** Раздел ещё не реализован: пункт показывается неактивным. */
  planned?: string
}

const sections: Section[] = [
  { path: '/categories', label: 'Категории', permission: 'catalog' },
  { path: '/attributes', label: 'Атрибуты', permission: 'catalog' },
  { path: '/products', label: 'Товары', permission: 'catalog' },
  { path: '/stores', label: 'Мои магазины', permission: 'catalog' },
  { path: '/checkout', label: 'Касса', permission: 'stock_ops' },
  { path: '/sale', label: 'Быстрая продажа', permission: 'stock_ops' },
  { path: '/documents', label: 'Приход и перемещение', permission: 'stock_ops' },
  { path: '/inventory', label: 'Инвентаризация', permission: 'stock_adjust' },
  { path: '/movements', label: 'Журнал движений', permission: 'movements' },
  { path: '/warehouses', label: 'Склады', permission: 'warehouses' },
  { path: '/imports', label: 'Импорт', permission: 'import_goods' },
  { path: '/index', label: 'Поисковый индекс', permission: 'index' },
]

export default function AppShell() {
  const { state, logout, switchCompany } = useAuth()
  if (state.status !== 'authenticated') return null

  const { me } = state
  const inCompany = me.company !== null

  return (
    <div className="min-h-svh">
      <header className="sticky top-0 z-30 border-b border-ink-200 bg-white dark:border-ink-800 dark:bg-ink-950">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex size-7 items-center justify-center rounded-lg bg-brand-600 text-xs font-semibold text-white">
              К
            </span>
            <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Каталог и склад
            </span>
          </div>

          {inCompany && (
            <span className="rounded-md bg-brand-50 dark:bg-brand-500/15 px-2 py-1 text-xs font-medium text-brand-700 dark:text-brand-300">
              {me.company?.name}
            </span>
          )}

          <div className="ml-auto flex items-center gap-4">
            {me.is_platform && inCompany && (
              <button
                type="button"
                onClick={() => switchCompany(null)}
                className="text-xs font-medium text-ink-500 transition-colors hover:text-brand-600"
              >
                ← Ко всем компаниям
              </button>
            )}
            <div className="text-right">
              <div className="text-sm text-ink-900 dark:text-ink-50">{me.full_name}</div>
              <div className="text-xs text-ink-500">{me.role_title}</div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="rounded-md border border-ink-300 px-3 py-1.5 text-xs font-medium text-ink-700 transition-colors hover:border-danger-500 hover:text-danger-700 dark:border-ink-700 dark:text-ink-300"
            >
              Выйти
            </button>
          </div>
        </div>
      </header>

      {/* Сотрудник платформы, не выбравший компанию, работает на её уровне:
          разделы каталога относятся к данным конкретной компании. */}
      {me.is_platform && !inCompany ? (
        <main className="mx-auto max-w-6xl px-6 py-8">
          <PlatformPage />
        </main>
      ) : (
        <div className="flex">
          <Sidebar permissions={me.permissions.read} />
          <main className="min-w-0 flex-1 px-6 py-6">
            <Routes>
              <Route path="/" element={<Navigate to="/categories" replace />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/attributes" element={<AttributesPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductCardPage />} />
              <Route path="/stores" element={<StoresPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/sale" element={<SalePage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/movements" element={<MovementsPage />} />
              <Route path="/warehouses" element={<WarehousesPage />} />
              <Route path="/imports" element={<ImportsPage />} />
              <Route path="/index" element={<IndexPage />} />
              {sections
                .filter((s) => s.planned)
                .map((s) => (
                  <Route
                    key={s.path}
                    path={s.path}
                    element={<PlannedSection title={s.label} stage={s.planned!} />}
                  />
                ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
      )}
    </div>
  )
}

function Sidebar({ permissions }: { permissions: Record<string, boolean> }) {
  return (
    <nav
      aria-label="Разделы"
      className="w-52 shrink-0 border-r border-ink-200 px-3 py-6 dark:border-ink-800"
    >
      <ul className="space-y-0.5">
        {sections
          .filter((s) => permissions[s.permission] !== false)
          .map((s) => (
            <li key={s.path}>
              <NavLink
                to={s.path}
                className={({ isActive }) =>
                  `flex items-baseline justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-500/15 font-medium text-brand-700 dark:text-brand-300'
                      : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900 dark:hover:text-ink-50 dark:text-ink-400 dark:hover:bg-ink-900'
                  }`
                }
              >
                <span>{s.label}</span>
                {s.planned && <span className="text-2xs text-ink-400">{s.planned}</span>}
              </NavLink>
            </li>
          ))}
      </ul>
    </nav>
  )
}

function PlannedSection({ title, stage }: { title: string; stage: string }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">{title}</h2>
      <div className="mt-4 rounded-lg border border-dashed border-ink-300 px-6 py-10 text-center dark:border-ink-700">
        <p className="text-sm text-ink-700 dark:text-ink-300">Раздел в разработке</p>
        <p className="mt-1.5 text-sm text-ink-500">
          Схема данных заведена, обработчики и интерфейс появятся на этапе «{stage}».
        </p>
      </div>
    </section>
  )
}

function NotFound() {
  return (
    <section className="py-16 text-center">
      <p className="text-sm text-ink-700 dark:text-ink-300">Страница не найдена</p>
      <p className="mt-1.5 text-sm text-ink-500">Выберите раздел в списке слева.</p>
    </section>
  )
}

export function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-svh items-center justify-center px-4">{children}</div>
}
